'use client';

/**
 * useSessionMembers — resolve a group-chat session's roster to display names
 * (#8198, realtime W1 identity layer).
 *
 * The realtime layer (`useSessionRealtime`) reports presence and typing as raw
 * `user_id` strings. To render "Alice, Bob online" / "Charlie is typing…" those
 * ids must be resolved to human labels. This hook fetches the roster once per
 * session via `getSessionMembers` and exposes a memoized
 * `Map<user_id, SessionMemberInfo>` plus a `resolveName(userId)` helper that
 * falls back to a compact id fragment when an id isn't in the roster (e.g. a
 * group grantee id, or a presence frame that raced ahead of the roster fetch).
 *
 * Only call this for GROUP sessions — the roster endpoint is meaningful only
 * when a session has been shared. The consuming component (`GroupPresenceBar`)
 * is mounted by the integrator only for group sessions; pass `enabled: false`
 * to short-circuit the fetch in any other context.
 *
 * Roster data is server state, cached in a shared `CacheManager` (2-min TTL,
 * matching the sessions-list cache window) so re-mounts and sibling components
 * reuse one fetch. The map reference is stable across renders while cached, so
 * `resolveName`'s memoization holds.
 */

import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { CacheManager } from '@/lib/cache/CacheManager';
import sessionServices, {
  type SessionMember,
} from '@/services/session-services';

interface SessionMemberInfo {
  name: string | null;
  email: string | null;
  /**
   * Avatar image URL. The members roster carries no avatar today, so this is
   * always undefined for now — consumers fall back to initials. Kept in the
   * shape so a future roster field flows through without a signature change.
   */
  avatarUrl?: string | undefined;
}

type SessionMemberMap = Map<string, SessionMemberInfo>;

// 2-minute TTL — mirrors the sessions-list cache window (see CLAUDE.md
// in-memory caches). A roster rarely changes mid-conversation; a slightly stale
// display name is acceptable and cheap to refresh.
const membersCache = new CacheManager<SessionMemberMap>(
  'SessionMembers',
  2 * 60 * 1000
);

/**
 * Compact, stable fallback label for a user id that isn't in the roster.
 * Never localized — it's an id fragment, not prose.
 */
export function shortUserLabel(userId: string): string {
  const trimmed = userId.trim();
  if (trimmed.length === 0) return '?';
  return trimmed.length <= 8 ? trimmed : trimmed.slice(0, 8);
}

/** Build the memoizable `user_id → info` map from a raw members list. */
export function buildMemberMap(
  members: readonly SessionMember[]
): SessionMemberMap {
  const map: SessionMemberMap = new Map();
  for (const member of members) {
    map.set(member.grantee_id, {
      name: member.name,
      email: member.email,
      avatarUrl: member.avatar_url ?? undefined,
    });
  }
  return map;
}

/**
 * Best human label for a user id, resolving name → email → id fragment.
 * Pure so it's unit-testable independent of React.
 */
export function resolveMemberName(
  map: SessionMemberMap,
  userId: string
): string {
  const info = map.get(userId);
  if (info) {
    // Treat blank/whitespace name & email as MISSING, not as a valid label — a
    // roster row can carry name="" (empty string, not null), which would slip
    // past a `?? ` nullish fallback and render an empty "?" avatar.
    const name = info.name?.trim();
    if (name) return name;
    const email = info.email?.trim();
    if (email) return email;
  }
  return shortUserLabel(userId);
}

interface UseSessionMembersResult {
  /** Memoized `user_id → { name, email, avatarUrl? }`. Empty until loaded. */
  membersById: SessionMemberMap;
  /** Resolve a user id to a display label (name → email → short id). */
  resolveName: (userId: string) => string;
  isLoading: boolean;
  /** True when the roster fetch failed; the map stays empty and names fall back. */
  error: boolean;
}

const EMPTY_MAP: SessionMemberMap = new Map();

export function useSessionMembers(
  sessionId: string | null,
  options: { enabled?: boolean } = {}
): UseSessionMembersResult {
  const { enabled = true } = options;
  // Auth gate — see the fetch effect below for why this is load-bearing.
  const { isLoading: isAuthLoading, isAuthenticated } = useAuth();

  const [membersById, setMembersById] = useState<SessionMemberMap>(() =>
    sessionId ? (membersCache.get(sessionId) ?? EMPTY_MAP) : EMPTY_MAP
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled || !sessionId) {
      setMembersById(EMPTY_MAP);
      return;
    }

    // Wait for auth before fetching. The roster endpoint is user-scoped, and the
    // bearer token is mirrored into per-tab `sessionStorage` — which starts EMPTY
    // in a freshly-opened tab. Firing on mount therefore raced the token: in a
    // new tab the request went out unauthenticated and came back as a non-success
    // envelope, so the roster emptied and no avatars rendered. Reloading the same
    // tab appeared to "fix" it only because sessionStorage survives a reload, so
    // the token was already present at mount. Gating on `isAuthLoading` removes
    // the race in both cases; the effect re-runs once auth resolves.
    if (isAuthLoading || !isAuthenticated) {
      return;
    }

    const cached = membersCache.get(sessionId);
    if (cached) {
      setMembersById(cached);
      setError(false);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(false);
    void (async () => {
      try {
        const res = await sessionServices.getSessionMembers(sessionId);
        if (!active) return;
        if (res.status === 'success') {
          const map = buildMemberMap(res.data.members);
          membersCache.set(sessionId, map);
          setMembersById(map);
        } else {
          setError(true);
        }
      } catch {
        // Fail soft — an unresolved roster degrades to id-fragment labels, it
        // never breaks the presence UI. But it IS logged: silently swallowing
        // this made a failing /members request indistinguishable from "nobody is
        // online", since both render an empty avatar stack.
        if (active) setError(true);
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [enabled, sessionId, isAuthLoading, isAuthenticated]);

  const resolveName = useCallback(
    (userId: string): string => resolveMemberName(membersById, userId),
    [membersById]
  );

  return { membersById, resolveName, isLoading, error };
}
